import {
  BookingPromoIneligibilityReason,
  ProPromoCodeCustomerScope,
  PromotionPendingCondition,
  type BookingCardPromotionObject,
} from '@cfixe-monorepo/shared';
import type { BookingLabels, CartSummary } from '../types';

/* Promotions automatiques dans le tunnel (lot 2) : badge des cartes, rappel d'une remise sous condition
   avant le formulaire, bandeau quand la condition tombe à la pré-réservation. Montants et libellés viennent
   du serveur ; ici, seulement le choix de la phrase. */

export type PromotionDisplayLabels = Pick<
  BookingLabels,
  | 'promotion_condition_new_customer'
  | 'promotion_condition_tags'
  | 'promotion_condition_hours'
  | 'promotion_condition_place'
  | 'promotion_pending_new_customer'
  | 'promotion_pending_tags'
  | 'promotion_pending_hours'
  | 'promotion_pending_place'
  | 'promotion_rejected_new_customer'
  | 'promotion_rejected_tags'
  | 'promotion_rejected_generic'
>;

/** Condition principale d'une promotion : le client d'abord, puis les heures, puis le lieu. */
const mainCondition = (conditions: readonly PromotionPendingCondition[]): PromotionPendingCondition | undefined =>
  [PromotionPendingCondition.CUSTOMER, PromotionPendingCondition.HAPPY_HOURS, PromotionPendingCondition.PLACE].find(
    (condition): boolean => conditions.includes(condition),
  );

/** Badge d'une carte : « −20 % », « −15 % à certaines heures », « −10 % nouveaux clients ». */
export function promotionBadgeLabel(promotion: BookingCardPromotionObject, labels: PromotionDisplayLabels): string {
  const condition = mainCondition(promotion.conditions);
  const suffix =
    condition === PromotionPendingCondition.CUSTOMER
      ? promotion.customer_scope === ProPromoCodeCustomerScope.TAGS
        ? labels.promotion_condition_tags
        : labels.promotion_condition_new_customer
      : condition === PromotionPendingCondition.HAPPY_HOURS
        ? labels.promotion_condition_hours
        : condition === PromotionPendingCondition.PLACE
          ? labels.promotion_condition_place
          : '';
  return suffix ? `${promotion.value_label} ${suffix}` : promotion.value_label;
}

/**
 * Prix remisé d'une carte quand la remise est acquise : seulement si le serveur a chiffré la formule que la
 * carte affiche (son prix y figure), sinon rien — la carte garde le badge seul.
 */
export function promotionCardPrice(
  promotion: BookingCardPromotionObject | undefined,
  displayPrice: string | null,
): { before: string; after: string } | null {
  if (!promotion?.price_before || !promotion.price_after || !displayPrice) return null;
  if (!displayPrice.includes(promotion.price_before)) return null;
  return { before: displayPrice, after: displayPrice.replace(promotion.price_before, promotion.price_after) };
}

/** Rappel d'une remise annoncée sous condition (« −10,00 € nouveaux clients : vérifié avec votre email »). */
export function promotionPendingNotice(
  summary: Pick<CartSummary, 'promo_discount' | 'promo_pending_conditions' | 'promo_customer_scope'> | undefined,
  labels: PromotionDisplayLabels,
): string | undefined {
  const condition = mainCondition(summary?.promo_pending_conditions ?? []);
  if (!condition || !summary?.promo_discount) return undefined;
  const template =
    condition === PromotionPendingCondition.CUSTOMER
      ? summary.promo_customer_scope === ProPromoCodeCustomerScope.TAGS
        ? labels.promotion_pending_tags
        : labels.promotion_pending_new_customer
      : condition === PromotionPendingCondition.HAPPY_HOURS
        ? labels.promotion_pending_hours
        : labels.promotion_pending_place;
  return template.replace('{amount}', summary.promo_discount);
}

/** Bandeau d'une promotion automatique retirée à la pré-réservation (condition client tombée). */
export function promotionRejectedNotice(
  reason: BookingPromoIneligibilityReason | undefined,
  label: string | undefined,
  labels: PromotionDisplayLabels,
): string | undefined {
  if (!reason) return undefined;
  const template =
    reason === BookingPromoIneligibilityReason.NOT_FIRST_TIME_CUSTOMER
      ? labels.promotion_rejected_new_customer
      : reason === BookingPromoIneligibilityReason.CUSTOMER_NOT_TARGETED
        ? labels.promotion_rejected_tags
        : labels.promotion_rejected_generic;
  return template.replace('{label}', label ?? '');
}
